import * as Sentry from '@sentry/browser';
import amplitude from 'amplitude-js';
import useUserData from 'FeatureModules/Auth/hooks/useUserData';
import React, { createContext, FC, PropsWithChildren, useEffect } from 'react';
import { useFirebase } from '../FeatureModules/Auth/Contexts/FirebaseContext';

const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY;

interface IAmplitudeContextValues {
  logEvent: amplitude.AmplitudeClient['logEvent'];
}

const defaultLogEvent: amplitude.AmplitudeClient['logEvent'] = (
  event: string,
  // data is literally allowed to be anything.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any,
  callback?: amplitude.Callback,
  errorCallback?: amplitude.Callback,
  outOfSession?: boolean
) => {
  console.warn('unimplemented amplitude context', event);

  Sentry.captureMessage('unimplemented amplitude context', {
    extra: { event, data },
  });

  return undefined;
};

const AmplitudeContext = createContext<IAmplitudeContextValues>({
  logEvent: defaultLogEvent,
});

const AmplitudeConsumer = AmplitudeContext.Consumer;

const AmplitudeProvider: FC<PropsWithChildren> = ({ children }) => {
  const { authenticated } = useFirebase();
  const { user, company, refetch } = useUserData();

  const initAmplitude = (apiKey: string) => {
    amplitude.getInstance().init(apiKey);
  };

  const setAmplitudeUserId = (userId: string) => {
    amplitude.getInstance().setUserId(userId);
  };

  const setAmplitudeUserProperties = (properties: unknown) => {
    amplitude.getInstance().setUserProperties(properties);
  };

  // Function is used to send the actual events. The eventType is the name/type of the event and the eventProperties can be anything.
  const sendAmplitudeData: amplitude.AmplitudeClient['logEvent'] = (eventType, eventProperties) => {
    return amplitude.getInstance().logEvent(eventType, eventProperties);
  };

  // Initialise Amplitude on mount.
  useEffect(() => {
    // On mount amplitude should be initialised.
    if (!apiKey) return;

    initAmplitude(apiKey);
  }, []);

  // Handle user logging out and refetching the user/company if they login.
  useEffect(() => {
    if (!apiKey) return;
    if (!authenticated) {
      setAmplitudeUserId('-');
      setAmplitudeUserProperties({
        name: '-',
        email: '-',
        role: '-',
        company: '-',
        companyId: '-',
        plan: 'NO_PLAN',
      });

      sendAmplitudeData('SESSION_EXPIRED');
    } else {
      // If the authenticated status changes, make sure the user is fetched again from the cache.
      // This makes sure the correct user is added to amplitude.
      // ( if this is not refetched, logging in and out of different accounts causes amplitude to update with the old user.)
      refetch();
    }
  }, [authenticated, refetch]);

  // Handle user logging in, setting new properties.
  useEffect(() => {
    if (!apiKey) return;
    // If the user or company changes the amplitude properties should be reset accordingly.
    if (user && company) {
      setAmplitudeUserId(user.id);

      setAmplitudeUserProperties({
        name: user.fullName,
        email: user.email,
        role: user.role,
        company: company.name,
        companyId: company.id,
        plan: company.planDetails?.planType,
      });

      // Instead of having a LOGGED_IN event, we now have a SESSION_STARTED event that is send once the user data is available.
      // This can be triggered multiple times if a user reloads the page, but that is better than not having any login event.
      sendAmplitudeData('SESSION_STARTED');
    }
  }, [user, company]);

  return (
    <AmplitudeContext.Provider
      value={{
        logEvent: sendAmplitudeData,
      }}
    >
      {children}
    </AmplitudeContext.Provider>
  );
};

export { AmplitudeConsumer, AmplitudeProvider };

export default AmplitudeContext;
